
import React, { useEffect, useState } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import config from '../../config';
import { useApi } from '../../ApiContext';
import PopupService from '../PopupService';

const PAGE_SIZES = [10, 20, 50, 100];

const DisplayClientData = () => {
  const username = sessionStorage.getItem("username");
  const mrToken = localStorage.getItem("mr_token");
  const { callApi, setIsApiLoading } = useApi();

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [loading, setLoading] = useState(false);

  // Data table states
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('mr_upload_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    // Load clients
    const fetchClients = async () => {
      setLoading(true);
      await callApi(async () => {
        setIsApiLoading(true);
        try {
          const response = await fetch(`${config.API_BASE_URL}/activeclients`, {
            headers: {
              'Content-Type': 'application/json',
              'X-Username': username,
              'Authorization': `Bearer ${mrToken}`
            }
          });
          if (response.ok) {
            const data = await response.json();
            setClients(Array.isArray(data.data.ACTIVE_CLIENTS) ? data.data.ACTIVE_CLIENTS : []);
          }
        } catch (err) {
          setClients([]);
        } finally {
          setLoading(false);
          setIsApiLoading(false);
        }
      });
    };
    fetchClients();
  }, []);

  // Load client data
  const loadClientData = async (params = {}) => {
    const clientIdToUse = params.clientId !== undefined ? params.clientId : selectedClientId;
    if (!clientIdToUse) return;
    setLoading(true);
    await callApi(async () => {
      setIsApiLoading(true);
      try {
        const payload = {
          clientId: clientIdToUse,
          pageNo: params.pageNo || pageNo,
          pageSize: params.pageSize || pageSize,
          searchTerm: params.searchTerm !== undefined ? params.searchTerm : searchTerm,
          sortField: params.sortField || sortField,
          sortOrder: params.sortOrder || sortOrder
        };
        const response = await fetch(`${config.API_BASE_URL}/clientdata/loadClientData`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Username': username,
            'Authorization': `Bearer ${mrToken}`
          },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const resData = await response.json();
          const result = resData.data;
          setData(result.SEARCH_LIST || []);
          setTotal(result.total || 0);


		  if (result.SEARCH_LIST && result.SEARCH_LIST.length > 0) {
		    const allCols = Object.keys(result.SEARCH_LIST[0]).filter(
		      col => col !== '_id' && col !== 'unique_hash' && col !== 'hash_tags'
		    );
		    const orderedCols = [];
			if (allCols.includes('source')) orderedCols.push('source');
		    if (allCols.includes('filename')) orderedCols.push('filename');
		    if (allCols.includes('file_path')) orderedCols.push('file_path');
		    allCols.forEach(col => {
		      if (!orderedCols.includes(col)) orderedCols.push(col);
		    });
		    setColumns(orderedCols);
		  } else {
		    setColumns([]);
		  }


        }
      } catch (err) {
        setData([]);
        setTotal(0);
        setColumns([]);
      } finally {
        setLoading(false);
        setIsApiLoading(false);
      }
    });
  };

  // Handle client selection
  const handleClientChange = async (e) => {
    const newClientId = e.target.value;
    setSelectedClientId(newClientId);
    setPageNo(1);
    setSearchTerm('');
    setSearchInput('');
    setSortField('mr_upload_date');
    setSortOrder('desc');
    await loadClientData({ clientId: newClientId, pageNo: 1, searchTerm: '' });
  };

  // Handle search
  const handleSearch = async () => {
    setSearchTerm(searchInput);
    setPageNo(1);
    await loadClientData({ pageNo: 1, searchTerm: searchInput });
  };

  // Handle clear search
  const handleClearSearch = async () => {
    setSearchInput('');
    setSearchTerm('');
    setPageNo(1);
    await loadClientData({ pageNo: 1, searchTerm: '' });
  };

  // Handle page size change
  const handlePageSizeChange = async (e) => {
    setPageSize(Number(e.target.value));
    setPageNo(1);
    await loadClientData({ pageNo: 1, pageSize: Number(e.target.value) });
  };

  // Handle page navigation
  const handlePageChange = async (newPageNo) => {
    setPageNo(newPageNo);
    await loadClientData({ pageNo: newPageNo });
  };

  // Handle sort
  const handleSort = async (field) => {
    let order = sortOrder;
    if (sortField === field) {
      order = sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      order = 'desc';
    }
    setSortField(field);
    setSortOrder(order);
    await loadClientData({ sortField: field, sortOrder: order });
  };

  // Handle jump to page
  const [jumpPage, setJumpPage] = useState('');
  const handleJumpToPage = async (e) => {
    if (e.key === 'Enter') {
      let page = Number(jumpPage);
      if (page > 0 && page <= Math.ceil(total / pageSize)) {
        setPageNo(page);
        await loadClientData({ pageNo: page });
      }
    }
  };

  // Render pagination
  const totalPages = Math.ceil(total / pageSize);
  const pageNumbers = [];
  let startPage = Math.max(1, pageNo - 4);
  let endPage = Math.min(totalPages, startPage + 9);
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <Header />
      <div className="container-fluid text-secondary">
        <br /><br /><br /><br />
        <div className="card">
          <div className="card-header">View Client Data</div>
          <div className="card-body custom-card-bg">
            <div className="card">
              <div className='card-body'>
                <div className="tab-content p-3 border-0">
                  <div className='row'>
                    <div className="form-group col-md-3">
                      <label htmlFor="client_select" className="text-info fw-bold">Select Client</label>
                      <select
                        id="client_select"
                        className="form-select"
                        value={selectedClientId}
                        onChange={handleClientChange}
                      >
                        <option value="">Select Client</option>
                        {clients.map((client, idx) => (
                          <option key={idx} value={client.clientId || client.id}>
                            {client.clientName || client.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div id="client_data" className="mt-3">
                    {selectedClientId && (
                      <>
                        <div className="d-flex mb-2">
                          <input
                            type="text"
                            className="form-control w-auto me-2"
                            placeholder="Search..."
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                          />
                          <button className="btn btn-primary me-2" onClick={handleSearch}>
                            <i className="fa fa-search"></i>
                          </button>
                          <button className="btn btn-secondary me-2" onClick={handleClearSearch}>
                            <i className="fa fa-trash"></i>
                          </button>
                          <select className="form-select w-auto" value={pageSize} onChange={handlePageSizeChange}>
                            {PAGE_SIZES.map(size => (
                              <option key={size} value={size}>{size}</option>
                            ))}
                          </select>
                        </div>
                        <div className="table-responsive">
                          <table className="table table-bordered table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                {columns.map(col => (
                                  <th key={col} style={{ cursor: 'pointer' }} onClick={() => handleSort(col)}>
                                    {col}
                                    {sortField === col && (
                                      <span>{sortOrder === 'desc' ? ' 🔽' : ' 🔼'}</span>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {data.length === 0 && (
                                <tr>
                                  <td colSpan={columns.length + 1} className="text-center">No data found</td>
                                </tr>
                              )}
                              {data.map((row, idx) => (
                                <tr key={idx}>
                                  <td>{(pageNo - 1) * pageSize + idx + 1}</td>

								  {columns.map(col => (
								    <td className="text-nowrap" key={col}>
								      {typeof row[col] === 'object' && row[col] !== null
								        ? JSON.stringify(row[col])
								        : row[col]}
								    </td>
								  ))}

                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div>
                            <button className="btn btn-outline-secondary btn-sm me-1" disabled={pageNo === 1} onClick={() => handlePageChange(1)}>First</button>
                            <button className="btn btn-outline-secondary btn-sm me-1" disabled={pageNo === 1} onClick={() => handlePageChange(pageNo - 1)}>Previous</button>
                            {pageNumbers.map(num => (
                              <button
                                key={num}
                                className={`btn btn-sm me-1 ${num === pageNo ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => handlePageChange(num)}
                              >
                                {num}
                              </button>
                            ))}
                            <button className="btn btn-outline-secondary btn-sm me-1" disabled={pageNo === totalPages} onClick={() => handlePageChange(pageNo + 1)}>Next</button>
                            <button className="btn btn-outline-secondary btn-sm" disabled={pageNo === totalPages} onClick={() => handlePageChange(totalPages)}>Last</button>
                          </div>
						  <div className="d-flex align-items-center">
						      <span className="me-3">
						        Page {pageNo} of {totalPages} pages
						      </span>
						      <input
						        type="number"
						        min="1"
						        max={totalPages}
						        value={jumpPage}
						        onChange={e => setJumpPage(e.target.value)}
						        onKeyDown={handleJumpToPage}
						        className="form-control w-auto d-inline-block me-2"
						        placeholder="Jump to page"
						        style={{ width: '100px' }}
						      />
						      <span>Total: {total} records</span>
						    </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DisplayClientData;
